import { useStaticQuery, graphql } from "gatsby";
import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { useIntl } from "gatsby-plugin-intl";

import KCSuccessSpinner from "Components/common/KCSuccessSpinner";
import KCButton from "Components/KCButton";
import { handlerEmailSignUp } from "../../../emailUtils";
import { GlobalDispatchContext, GlobalStateContext } from "../../context";

const StyledBanner = styled.div`
  background-color: ${(props) => props.theme.color.smoke};
  display: ${(props) => (props.isShown ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  padding: 0.4rem 1rem 1rem 1rem;
  width: 100%;
  font-size: 0.9rem;
  position: relative;
  ${(props) => props.theme.responsive.mobile} {
    font-size: 0.8rem;
  }
`;

const StyledHeader = styled.div`
  display: flex;
  width: 100%;
  margin-top: 0.4rem;
`;

const StyledContent = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const StyledSubtitle = styled.div`
  font-size: 0.9rem;
  font-weight: 600;
  flex: 1;
`;

const StyledCloseButton = styled.div`
  margin-left: 1rem;
  font-size: 0.6rem;
  text-decoration: underline;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const StyledInputWrapper = styled.div`
  display: flex;
  margin-top: 0.6rem;
  display: flex;
  background-color: white;
  padding: 0.2rem;
`;

const StyledInput = styled.input`
  flex: 1;
  border: 0;
  outline: none;
  padding: 0.2rem;
`;

const StyledError = styled.div`
  margin-top: 0.3rem;
  font-size: 0.6rem;
  color: ${(props) => props.theme.color.red};
`;

const StyledSuccessWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.isShown ? 1 : 0)};
  transition: opacity 0.3s;
  pointer-events: ${(props) => (props.isShown ? "default" : "none")};
`;
const StyledSuccessContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const StyledSuccessText = styled.div`
  font-weight: 600;
  font-size: 0.9rem;
`;

const StyledSuccessOverlay = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: ${(props) => props.theme.color.smoke};
  opacity: 0.95;
  z-index: -1;
`;

const StyledSuccessDismiss = styled.div`
  color: ${(props) => props.theme.color.grey};
  font-size: 0.7rem;
  &:hover {
    cursor: pointer;
    opacity: 0.8;
  }
`;

const Banner = ({ storeContext, ...props }) => {
  const { strapi } = useStaticQuery(graphql`
    query {
      strapi {
        addToBagPopup {
          bannerTitle {
            en
            es
            it
          }
          isBannerEnabled
          bannerDiscountCode
        }
      }
    }
  `);
  const intl = useIntl();
  const dispatch = useContext(GlobalDispatchContext);
  const state = useContext(GlobalStateContext);
  const { email, shopifyCheckout } = state;
  const [isShown, setIsShown] = useState(false);
  const [inputText, setInputText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    let numLineItems = shopifyCheckout?.lineItems?.length || 0;
    let numDiscounts = shopifyCheckout?.discountApplications.length || 0;

    if (numLineItems > 0 && (email === undefined || numDiscounts === 0)) {
      setIsShown(true);
    } else {
      setIsShown(false);
    }
  }, [
    email,
    shopifyCheckout?.lineItems?.length || 0,
    shopifyCheckout?.discountApplications.length || 0,
  ]);

  const handleEmailSignUp = () => {
    handlerEmailSignUp({
      shopifyClient: storeContext.client,
      email: inputText,
      checkoutID: shopifyCheckout.id,
      handleUpdateCheckout: (checkout) => {
        dispatch({
          type: "SET_SHOPIFY_CHECKOUT",
          shopifyCheckout: checkout,
        });
      },
      handleLoading: () => {
        setIsLoading(true);
        setIsError(false);
        setIsSuccess(false);
      },
      handleSuccess: () => {
        setIsLoading(false);
        setIsError(false);
        setIsSuccess(true);
        setInputText("");
      },
      handleError: () => {
        setIsLoading(false);
        setIsError(true);
        setIsSuccess(false);
        setInputText("");
      },
      sendgridList: ["2af4e6ae-6b52-4b5b-8e46-425d9dc5775d"],
      discountCode: strapi?.addToBagPopup?.bannerDiscountCode ?? undefined,
    });
  };

  const resetState = () => {
    setIsLoading(false);
    setIsError(false);
    setIsSuccess(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === `Enter`) {
      handleEmailSignUp();
    }
  };

  return (
    <StyledBanner isShown={isShown && strapi?.addToBagPopup?.isBannerEnabled}>
      <StyledSuccessWrapper isShown={isSuccess}>
        <StyledSuccessOverlay />
        <KCSuccessSpinner isShown={isSuccess} />
        <StyledSuccessContent>
          <StyledSuccessText>
            {strapi?.addToBagPopup?.bannerDiscountCode
              ? intl.formatMessage({
                  id: `buyPopup.banner.discountSuccess`,
                })
              : intl.formatMessage({
                  id: `buyPopup.banner.newsletterSuccess`,
                })}
          </StyledSuccessText>
          <StyledSuccessDismiss onClick={() => setIsShown(false)}>
            {intl.formatMessage({ id: `buyPopup.banner.close` })}
          </StyledSuccessDismiss>
        </StyledSuccessContent>
      </StyledSuccessWrapper>
      <StyledContent>
        <StyledHeader>
          <StyledSubtitle>
            {strapi?.addToBagPopup?.bannerTitle?.[intl.locale] ?? ""}
          </StyledSubtitle>
          <StyledCloseButton onClick={() => setIsShown(false)}>
            {intl.formatMessage({ id: `buyPopup.banner.noThanks` })}
          </StyledCloseButton>
        </StyledHeader>
        <StyledInputWrapper>
          <StyledInput
            isSuccess={isSuccess}
            onChange={(e) => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Email"
          />
          <KCButton
            isLoading={isLoading}
            isError={isError}
            isDisabled={isSuccess}
            handleClick={() => handleEmailSignUp()}
            label={
              isSuccess
                ? intl.formatMessage({
                    id: `buyPopup.banner.success`,
                  })
                : intl.formatMessage({
                    id: `buyPopup.banner.signUp`,
                  })
            }
            buttonType="primary"
            size="xs"
          />
        </StyledInputWrapper>
        {isError && (
          <StyledError>
            {intl.formatMessage({ id: `buyPopup.banner.error` })}
          </StyledError>
        )}
      </StyledContent>
    </StyledBanner>
  );
};

export default Banner;
